import {assessmentSerializer} from '@/providers/serializers/content/assessment';
import {collectionSerializer} from '@/providers/serializers/content/collection';
import {courseSerializer} from '@/providers/serializers/content/course';
import {unitSerializer} from '@/providers/serializers/content/unit';
import {lessonSerializer} from '@/providers/serializers/content/lesson';
import {questionSerializer} from '@/providers/serializers/content/question';
import {resourceSerializer} from '@/providers/serializers/content/resource';
import {rubricSerializer} from '@/providers/serializers/content/rubric';
import { competencySerializer } from '../competency/competency';
import { sessionService } from '@/providers/services/auth/session';
import { DEFAULT_CATALOG_STRING, DEFAULT_IMAGES_PATH } from '@/utils/constants';
import { taxonomySerializer } from '../content/taxonomy';
import { findAnyCompetencyTitle, getResourceFormat } from '@/utils/utils';

export class SearchSerializer {

  static get instance() {
    return this.INSTANCE;
  }
  private static INSTANCE = new SearchSerializer();

  public serializeLearningMapData(learningMapData: any) {
    const learningMapContents = learningMapData.contents;
    const assessmentContents = learningMapContents.assessment;
    const collectionContents = learningMapContents.collection;
    const courseContents = learningMapContents.course;
    const lessonContents = learningMapContents.lesson;
    const questionContents = learningMapContents.question;
    const resourceContents = learningMapContents.resource;
    const unitContents = learningMapContents.unit;
    const rubricContents = learningMapContents.rubric;
    const serializedAssessmentContents = assessmentContents.searchResults.map((assessment: any) => {
      return assessmentSerializer.serializeAssessment(assessment);
    });
    const serializedCollectionContents = collectionContents.searchResults.map((collection: any) => {
      return collectionSerializer.serializeCollection(collection);
    });

    const serializedCourseContents = courseContents.searchResults.map((course: any) => {
      return courseSerializer.serializeCourse(course);
    });

    const serializedQuestionContents = questionContents.searchResults.map((question: any) => {
      return questionSerializer.serializeQuestion(question);
    });

    const serializedResourceContents = resourceContents.searchResults.map((resource: any) => {
      return resourceSerializer.serializeResource(resource);
    });

    const serializedUnitContents = unitContents.searchResults.map((unit: any) => {
      return unitSerializer.serializeUnit(unit);
    });

    const serializedLessonContents = lessonContents.searchResults.map((lesson: any) => {
      return lessonSerializer.serializeLesson(lesson);
    });

    const serializedRubricContents = rubricContents.searchResults.map((rubric: any) => {
      return rubricSerializer.serializeRubric(rubric);
    });

    const signatureContents = learningMapData.signatureContents;

    const signatureAssessments = signatureContents.assessments;
    const signatureCollections = signatureContents.collections;
    const serializedSignatureAssessments = signatureAssessments.map((assessment: any) => {
      return assessmentSerializer.serializeSignatureAssessment(assessment);
    });

    const serializedSignatureCollections = signatureCollections.map((collection: any) => {
      return collectionSerializer.serializeSignatureCollection(collection);
    });

    const serializedLearningMapData = {
      contents: {
        collection: serializedCollectionContents,
        assessment: serializedAssessmentContents,
        course: serializedCourseContents,
        unit: serializedUnitContents,
        lesson: serializedLessonContents,
        resource: serializedResourceContents,
        question: serializedQuestionContents,
        rubric: serializedRubricContents,
      },
      totalCounts: {
        collection: learningMapContents.collection.totalHitCount,
        assessment: learningMapContents.assessment.totalHitCount,
        course: learningMapContents.course.totalHitCount,
        unit: learningMapContents.unit.totalHitCount,
        lesson: learningMapContents.lesson.totalHitCount,
        resource: learningMapContents.resource.totalHitCount,
        question: learningMapContents.question.totalHitCount,
        rubric: learningMapContents.rubric.totalHitCount,
      },
      signatureAssessments: serializedSignatureAssessments,
      signatureCollections: serializedSignatureCollections,
      prerequisites: competencySerializer.serializePrerequisites(learningMapData.prerequisites),
    };
    return serializedLearningMapData;
  }

  /**
   * Normalize the Search collections response
   *
   * @param payload is the endpoint response in JSON format
   * @returns {Collection[]}
   */
    public normalizeSearchCollection(payload: any) {
      const serializer = this;
      const resultSet = {
        searchResults: [],
        hitCount: payload.totalHitCount,
      };
      if (Array.isArray(payload.searchResults)) {
        const results = payload.searchResults.map((result: any) => {
          return serializer.normalizeCollection(result);
        });
        resultSet.searchResults = results;
      }
      return resultSet;
    }

    /**
     * Normalize a collection
     * @param {*} collectionData
     * @returns {Collection}
     */
    public normalizeCollection(collectionData: any) {
      const cdnUrls = sessionService.getCdnUrl();
      const contentCdnUrl = cdnUrls.content_cdn_url;
      const basePath =  contentCdnUrl;
      const appRootPath = window.location.href; // configuration appRootPath
      const userBasePath = cdnUrls.user_cdn_url;

      const thumbnailUrl = collectionData.thumbnail
        ? basePath + collectionData.thumbnail
        : DEFAULT_IMAGES_PATH.collection;
      const userThumbnailUrl = collectionData.userProfileImage
        ? userBasePath + collectionData.userProfileImage
        : DEFAULT_IMAGES_PATH.profile;
      const creatorThumbnailUrl = collectionData.creatorProfileImage
        ? userBasePath + collectionData.creatorProfileImage
        : DEFAULT_IMAGES_PATH.profile;
      const taxonomyInfo =
        (collectionData.taxonomySet &&
          collectionData.taxonomySet.curriculum &&
          collectionData.taxonomySet.curriculum.curriculumInfo) ||
        [];

      const course = collectionData.course || {};
      const standards = taxonomySerializer.normalizeTaxonomyArray(taxonomyInfo, false);
      return {
        id: collectionData.id,
        title: collectionData.title,
        description: collectionData.description || findAnyCompetencyTitle(standards) || collectionData.title,
        type: collectionData.type ? collectionData.type : collectionData.format,
        thumbnailUrl,
        standards,
        publishStatus: collectionData.publishStatus,
        learningObjectives: collectionData.languageObjective || findAnyCompetencyTitle(standards),
        resourceCount: collectionData.resourceCount || 0,
        questionCount: collectionData.questionCount || 0,
        remixCount: collectionData.scollectionRemixCount || 0,
        course: course.title,
        courseId: course.id,
        isVisibleOnProfile: collectionData.profileUserVisibility,
        owner: {
          id: collectionData.gooruUId,
          firstName: collectionData.userFirstName,
          lastName: collectionData.userLastName,
          avatarUrl: userThumbnailUrl,
          username: collectionData.usernameDisplay,
        },
        creator: {
          id: collectionData.creatorId,
          firstName: collectionData.creatorFirstname,
          lastName: collectionData.creatorLastname,
          avatarUrl: creatorThumbnailUrl,
          username: collectionData.creatornameDisplay,
        },
        taxonomySet: collectionData.taxonomySet,
        createdDate: collectionData.addDate,
        collaboratorIDs: collectionData.collaboratorIds,
        grade: collectionData.grade,
        instructionalModel: collectionData.instructionalMethod,
        lastModified: collectionData.lastModified,
        lastModifiedBy: collectionData.lastModifiedBy,
        license: collectionData.license,
        audience: collectionData.audience,
        keyPoints: collectionData.keyPoints,
        efficacy: collectionData.efficacy ? collectionData.efficacy : null,
        relevance: collectionData.relevance ? collectionData.relevance : null,
        engagement: collectionData.engagement ? collectionData.engagement : null,
        publisherQualityIndicator: collectionData.publisherQualityIndicator,
      };
    }

  public nomalizeSearchResources(payload: any) {
    const resultSet = {
      searchResults: [],
      hitCount: payload.totalHitCount,
    };
    if (Array.isArray(payload.searchResults)) {
      const results = payload.searchResults.map((result: any) => {
        return this.normalizeResource(result);
      });
      resultSet.searchResults = results;
    }
    return resultSet;
  }

  public normalizeResource(resource: any) {
    const cdnUrls = sessionService.getCdnUrl();
    const contentCdnUrl = cdnUrls.content_cdn_url;
    const basePath =  contentCdnUrl;
    const appRootPath = window.location.href; // configuration appRootPath
    const userBasePath = cdnUrls.user_cdn_url;

    const format = getResourceFormat(resource.contentSubFormat);
    const taxonomyInfo =
      (resource.taxonomySet &&
        resource.taxonomySet.curriculum &&
        resource.taxonomySet.curriculum.curriculumInfo) ||
      [];
    const standards =  taxonomySerializer.normalizeTaxonomyArray(taxonomyInfo, false);
    return {
      id: resource.gooruOid,
      title: resource.title,
      description: resource.description || findAnyCompetencyTitle(standards) || resource.title,
      format,
      url: resource.url,
      subformat: resource.contentSubFormat,
      thumbnailUrl: resource.thumbnail
        ? basePath + resource.thumbnail
        : DEFAULT_IMAGES_PATH.unit,
      creator: resource.user
        ? taxonomySerializer.normalizeOwner(resource.user) : null,
      owner: resource.user ? taxonomySerializer.normalizeOwner(resource.user) : null,
      type: 'resource',
      standards,
      taxonomySet: resource.taxonomySet,
      taxonomySubject: resource.taxonomySet
        ? resource.taxonomySet.subject
        : null,
      taxonomyCourse: resource.taxonomySet ? resource.taxonomySet.course : null,
      taxonomyDomain: resource.taxonomySet ? resource.taxonomySet.domain : null,
      publishStatus: resource.publishStatus,
      publisher: resource.publisher ? resource.publisher[0] : null,
      efficacy: resource.efficacy ? resource.efficacy : null,
      relevance: resource.relevance ? resource.relevance : null,
      engagement: resource.engagement ? resource.engagement : null,
      publisherQualityIndicator: resource.publisherQualityIndicator,
    };
  }

  public normalizeSearchQuestions(payload: any) {
    const resultSet =  {
      searchResults: [],
      hitCount: payload.totalHitCount,
    };
    if (Array.isArray(payload.searchResults)) {
      const results = payload.searchResults.map((result: any) => {
        return this.normalizeQuestion(result);
      });
      resultSet.searchResults =  results;
    }
    return resultSet;
  }

  /**
   * Normalizes a question
   * @param {*} result
   * @returns {Question}
   */
   public normalizeQuestion(questionData: any) {
    const cdnUrls = sessionService.getCdnUrl();
    const contentCdnUrl = cdnUrls.content_cdn_url;
    const thumbnailUrl = questionData.thumbnail ?
      contentCdnUrl + questionData.thumbnail :
        `${window.location.origin}/` + DEFAULT_IMAGES_PATH.unit;
    const taxonomyInfo =
      (questionData.taxonomySet &&
        questionData.taxonomySet.curriculum &&
        questionData.taxonomySet.curriculum.curriculumInfo) ||
      [];
    const format = DEFAULT_CATALOG_STRING.question[questionData.contentSubFormat].name;
    const standards = taxonomySerializer.normalizeTaxonomyArray(taxonomyInfo, false);
    const description = questionData.description
    ? questionData.description
    : questionData.text;
    return {
      id: questionData.gooruOid,
      title: questionData.title,
      subformat: questionData.contentSubFormat,
      description: description || findAnyCompetencyTitle(standards) || questionData.title,
      type: questionData.contentFormat ? questionData.contentFormat : null,
      format,
      publisher: null, // TODO missing publisher at API response,
      thumbnailUrl,
      creator: questionData.creator
        ? taxonomySerializer.normalizeCreator(questionData.creator)
        : null,
      owner: questionData.user
        ? taxonomySerializer.normalizeOwner(questionData.user)
        : null,
      standards,
      taxonomySet: questionData.taxonomySet,
      taxonomySubject: questionData.taxonomySet
        ? questionData.taxonomySet.subject
        : null,
      taxonomyCourse: questionData.taxonomySet
        ? questionData.taxonomySet.course
        : null,
      taxonomyDomain: questionData.taxonomySet
        ? questionData.taxonomySet.domain
        : null,
      efficacy: questionData.efficacy ? questionData.efficacy : null,
      relevance: questionData.relevance ? questionData.relevance : null,
      engagement: questionData.engagement ? questionData.engagement : null,
      contentSubFormat: questionData.contentSubFormat,
      publisherQualityIndicator: questionData.publisherQualityIndicator,
    };
  }

  public normalizeGeneratorAI(payload: any) {
    const aiData: any = [];
    if (payload && payload.length) {
      payload.map((ai: any) => {
        aiData.push({
          contentType: ai.contentType,
          id: ai.id,
          isCurated: ai.isCurated,
          keywords: ai.keywords,
          language: ai.language,
          metadata: ai.metadata,
          title: ai.title,
          transcript: this.sanitizeText(ai.transcript),
        });
      });
    }
    return aiData;
  }

  public sanitizeText(text: string) {
    const val: string = text.replaceAll('\\n', '</br>');
    return val;
  }

  private serializedPrerequisites(payload: any) {
    const prerequisitesData: any = [];
    if (payload.length) {
      payload.map((prerequitites: any) => {
        prerequisitesData.push({
          code: prerequitites.code,
          id: prerequitites.id,
          title: prerequitites.title,
        });
      });
    }
    return prerequisitesData;
  }

}

export const searchSerializer = SearchSerializer.instance;
